import './styles.scss';

import {
  EvaluationAnswers,
  saveQuestionnaireAnswer,
  toSaveQuestionnaireAnswerItems,
} from '@/pages/Users/api/services/saveQuestionnaireAnswer';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { buildAnswersFromCategories, countCompleted } from '@/pages/Users/components/EvaluationQuestionField/utils';
import { createNotification, handleError } from '@/utils';

import { EvaluationPreviewFormTypes } from './types';
import EvaluationQuestionField from '@/pages/Users/components/EvaluationQuestionField';
import Form from '@/common/Form';
import HasPermission from '@/components/HasPermission';
import Modal from '@/common/Modal';
import { Toast } from '@/models';
import { TrainingChecklistTableTypes } from '../TrainingChecklistTable/types';
import { submitCategory } from '../../api/services/submitCategory';
import useConfirmation from '@/hooks/useConfirmation';
import useTrainingChecklistTableState from '@/pages/Users/components/TrainingChecklistTable/hook';

const PERMISSION_DENIED_MESSAGE = 'Access denied';
const NOT_AN_EVALUATOR_MESSAGE = "You don't have permission to evaluate this category.";

const toEvaluationError = <T,>(err: T): T | Error =>
  String((err as { message?: string })?.message ?? '').includes(PERMISSION_DENIED_MESSAGE) ? new Error(NOT_AN_EVALUATOR_MESSAGE) : err;

export type EvaluationPreviewModalProps = {
  categoryId: string | null;
  categoryName?: string;
  driverFirstName?: string;
  driverLastName?: string;
  onHide: () => void;
};

const EvaluationPreviewModal = ({
  categoryId,
  categoryName,
  driverFirstName,
  driverLastName,
  onHide,
}: EvaluationPreviewModalProps): ReactElement => {
  const [{ categoryWithQuestions, questions, show, totalCount, isSubmitted, refetchTable }] = useTrainingChecklistTableState(
    ({ state }) => {
      // we would lookup the category by name since it's possible the category id wont match (coming from 2 different tables)
      const categoryWithQuestions = categoryName ? state.categoryWithQuestionMap[categoryName] : null;
      const questions = categoryWithQuestions?.questions || [];
      const totalCount = questions.length;

      return [
        {
          categoryWithQuestions,
          questions,
          totalCount,
          show: totalCount > 0,
          isSubmitted: Boolean(categoryWithQuestions?.category?.submittedBy),
          refetchTable: state.reload,
        },
      ];
    }
  );

  const [completedCount, setCompletedCount] = useState(0);

  const confirmCancel = useConfirmation({
    Body: {
      title: 'Cancel Evaluation',
      message: `Are you sure you want to cancel this driver evaluation for "${driverFirstName} ${driverLastName}"? Unsaved changes will be lost.`,
    },
  });
  const confirmSubmit = useConfirmation({
    Body: {
      title: 'Evaluation Submission',
      message: `Are you sure you want to submit this driver evaluation for "${driverFirstName} ${driverLastName}"? Once this submitted changes cannot be made.`,
    },
  });

  const cannotEvaluate = !!categoryWithQuestions && !categoryWithQuestions.canEvaluate;
  const readOnly = !HasPermission.check('allowEditTrainingEvaluation') || isSubmitted || cannotEvaluate;
  // Read-only evaluations skip validation so historical records with empty notes display as-is.
  const validator = useMemo(() => (readOnly ? undefined : EvaluationPreviewFormTypes.validator(questions)), [readOnly, questions]);

  // Only the in-progress (editable) form has unsaved changes to lose - closing a submitted/read-only
  // preview doesn't need confirmation.
  const handleHide = async (): Promise<void> => {
    if (!readOnly) {
      try {
        await confirmCancel();
      } catch {
        return;
      }
    }
    onHide();
  };

  // Allow error to bubble up to caller
  const saveAnswers = async (answers: EvaluationAnswers) => {
    if (!answers) return;
    const res = await saveQuestionnaireAnswer(toSaveQuestionnaireAnswerItems(answers));
    if (res?.error) throw res.error;
  };

  const handleSaveAndFinishLater = async (values: EvaluationPreviewFormTypes.Value): Promise<void> => {
    // save answers
    try {
      await saveAnswers(values.answers);
      refetchTable();
      onHide();
      createNotification('Progress Saved', Toast.Type.SUCCESS, `${categoryName || 'Category'}`);
    } catch (err) {
      handleError(toEvaluationError(err), { notification: { title: `Save ${categoryName || 'Category'}` } });
    }
  };

  const handleSubmit = async (values: EvaluationPreviewFormTypes.Value): Promise<void> => {
    // display confirmation modal
    try {
      await confirmSubmit();
    } catch {
      return;
    }

    try {
      await saveAnswers(values.answers);
      const res = await submitCategory(categoryWithQuestions?.category?.id);
      if (res?.error) throw new Error(res.error.message);
      refetchTable();
      onHide();
    } catch (err) {
      handleError(toEvaluationError(err), { notification: { title: `Submit ${categoryName || 'Training Questionaire'}` } });
    }
  };

  const percentComplete = useMemo(
    () => (totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0),
    [completedCount, totalCount]
  );

  useEffect(() => {
    if (!show) return;
    // Extremely etch case:
    // it's possible the category id wont match between checkEvaluationEligibility and getEvaluation endpoint.
    // We rely on category name to look up the category.
    // This change introduces a race condition;
    // If a user changes the category name while another is previewing it, but didn't startEvaluation, we could end up with a stale UI.
    if (!questions?.length) {
      // we need to refetch the table to get the update categories.
      refetchTable();
      // tell the user to click evaluate again.
      createNotification(
        'Training questionaire was updated. Please try again.',
        Toast.Type.WARNING,
        `${categoryName || 'Training Questionaire'}`
      );
      // close the modal
      onHide();
    }
    setCompletedCount(countCompleted(buildInitialAnswers(categoryWithQuestions)));
  }, [show]);

  return (
    <Modal className="EvaluationPreviewModal" show={show} onHide={handleHide} size="lg">
      {/* No save mutation exists yet - onSubmit is a stub until the submit-category service is added. */}
      <div className="EvaluationPreviewModal-Header d-flex justify-content-between align-items-center w-100 mb-4">
        <div className="d-flex align-items-center flex-nowrap">
          <i className="sv sv-clipboard-check fs-3 bg-success-subtle text-green p-3 rounded-3 d-flex align-items-center justify-content-center me-2" />
          <div className="d-flex flex-column justify-content-center">
            <h5 className="fw-normal m-0">Driver Evaluation - {categoryName}</h5>
            <p className="text-muted m-0">
              {completedCount} of {totalCount} Items Completed
            </p>
          </div>
        </div>
        <span className="fw-bold fs-5">{percentComplete}%</span>
      </div>
      <Form
        key={categoryId}
        value={{ answers: buildInitialAnswers(categoryWithQuestions) }}
        validator={validator}
        onSubmit={percentComplete === 100 ? handleSubmit : handleSaveAndFinishLater}
        onChange={(values: EvaluationPreviewFormTypes.Value) => setCompletedCount(countCompleted(values.answers))}
      >
        {cannotEvaluate && !isSubmitted && <div className="alert alert-warning py-2">{NOT_AN_EVALUATOR_MESSAGE}</div>}
        <div className="EvaluationPreviewModal-Questions">
          <h5 className="mb-3">{categoryName}</h5>
          {questions.map(({ question, options }, index) => (
            <EvaluationQuestionField key={question.id} index={index} question={question} options={options} readOnly={readOnly} />
          ))}
        </div>
        <div className="EvaluationPreviewModal-Footer">
          <Form.Button type="button" variant="outline-primary" onClick={handleHide}>
            {!readOnly ? 'Cancel' : 'Close'}
          </Form.Button>
          {!readOnly && (
            <div className="EvaluationPreviewModal-Actions">
              <Form.Watch name="answers">
                {([answers]: EvaluationAnswers[]): ReactElement | null => {
                  const missingNotes = EvaluationPreviewFormTypes.getMissingNoteIndexes(questions, answers);
                  if (!missingNotes.length) return null;
                  return (
                    <span className="EvaluationPreviewModal-Error">
                      Notes are required for item{missingNotes.length > 1 ? 's' : ''} {missingNotes.map((index) => index + 1).join(', ')}
                    </span>
                  );
                }}
              </Form.Watch>
              <Form.Button type="submit" variant="primary">
                {percentComplete === 100 ? 'Submit Evaluation' : 'Save and Finish Later'}
              </Form.Button>
            </div>
          )}
        </div>
      </Form>
    </Modal>
  );
};

const buildInitialAnswers = (category: TrainingChecklistTableTypes.CategoryWithQuestions | null): EvaluationAnswers =>
  buildAnswersFromCategories(category ? [category] : []);

export default EvaluationPreviewModal;
